import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import cn from 'classnames';
import './BrandOnboardingNetworkPanel.scss';

import { updateBrandOnboardingObject } from '../../../Actions/OnboardingActions';
import { fetchBrandMerchants } from '../../../APIClient/merchants';
import { getBrand } from '../../../Helpers/user_helpers';
import { openIntercomChat } from '../../../Helpers/chat_helpers';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import BrandOnboardingProgressBar from '../BrandOnboardingProgressBar';

const BrandOnboardingNetworkPanel = props => {
  const { onboarding, user } = props;
  const brand = getBrand(user);

  const [preferredNetwork, setPreferredNetwork] = useState(onboarding.integratingMerchant);
  const [merchantOptions, setMerchantOptions] = useState([]);

  const selectMerchant = merchant => {
    if (props.isLoading) return;
    setPreferredNetwork(merchant.source);
    props.updateBrandOnboardingObject({ integratingMerchant: merchant.source });
  };

  const onNextClick = () => {
    if (!onboarding.integratingMerchant) return window.ALERT.error('Please select an affiliate network to proceed.');
    props.goToNextPanel();
  };

  useEffect(() => {
    if (!brand?.domain)
      return window.ALERT.error('You do not have a valid brand domain set. In order to process, please set a valid domain for your brand.');

    props.setIsLoading(true);
    fetchBrandMerchants(getBrand(user))
      .then(resp => {
        const { merchants } = resp;
        setMerchantOptions(merchants || []);

        const firstMerchant = merchants?.length ? merchants[0].source : null;
        if (!onboarding.integratingMerchant && firstMerchant) {
          setPreferredNetwork(firstMerchant);
          props.updateBrandOnboardingObject({ integratingMerchant: firstMerchant });
        }
      })
      .catch(err => console.error('Error fetching brand merchants:', err))
      .finally(() => props.setIsLoading(false));
  }, []);

  return (
    <div className='brand-onboarding-network-panel-outer'>
      <div className='brand-onboarding-network-panel-inner'>
        <div className='brand-onboarding-split-container-outer'>
          <div className='brand-onboarding-split-container-inner'>
            <div className='brand-onboarding-graphic-container-outer'>
              <div className='brand-onboarding-graphic-container-inner'>
                <div className='brand-onboarding-graphic-container-stat'>
                  <div className='brand-onboarding-graphic-container-stat-label'>Our Numbers</div>
                  <div className='brand-onboarding-graphic-container-stat-number'>{props.featuredStat.number}+</div>
                  <div className='brand-onboarding-graphic-container-stat-description'>{props.featuredStat.description}</div>
                </div>

                <BrandOnboardingProgressBar
                  goToPanel={props.goToPanel}
                  sections={props.sections}
                  activeSectionName={props.activeSectionName}
                  activePanelName={props.activePanelName}
                />
              </div>

              <div className='brand-onboarding-graphic-background'>
                <img className='brand-onboarding-graphic-background-image' src={props.backgroundImage} alt='name panel graphic' />
                <div className='brand-onboarding-graphic-background-overlay'></div>
              </div>
            </div>
            <div className='brand-onboarding-input-container-outer'>
              <div className='brand-onboarding-input-container-number-marker'>{props.panelNumber}</div>

              <div className={cn('brand-onboarding-input-container-inner', { isLoading: props.isLoading })}>
                <div className='brand-onboarding-title-text'>Commission Information</div>
                <div className='brand-onboarding-subtitle-text'>What affiliate network do you want to use to track orders?</div>

                <div className='brand-onboarding-network-panel-network-choice'>
                  <div className='brand-onboarding-network-panel-network-shopmy-title'>ShopMy</div>
                  <div className='brand-onboarding-network-panel-network-shopmy-subtitle'>
                    Integrate directly on our world-class network giving you access to our entire feature set. You will be required to add tracking
                    pixels for complete click and order integration.
                  </div>

                  <div className='brand-onboarding-network-panel-network-split-container'>
                    <div className='brand-onboarding-network-panel-network-shopmy-perks'>
                      <div className='brand-onboarding-network-panel-network-shopmy-perk'>
                        <FontAwesomeIcon icon={faCircleCheck} />
                        Assign custom discount codes
                      </div>
                      <div className='brand-onboarding-network-panel-network-shopmy-perk'>
                        <FontAwesomeIcon icon={faCircleCheck} />
                        Set rates for specific creators
                      </div>
                      <div className='brand-onboarding-network-panel-network-shopmy-perk'>
                        <FontAwesomeIcon icon={faCircleCheck} />
                        Manage orders on the platform
                      </div>
                    </div>

                    {/* For phase 1 & 2 release, keeping this hardcoded */}
                    <button className={cn('brand-onboarding-network-panel-button', { disabled: true })} onClick={() => openIntercomChat()}>
                      Chat with our team
                    </button>
                  </div>
                </div>

                {merchantOptions?.length ? (
                  <>
                    <div className='brand-onboarding-network-panel-other-networks-header'>Our current rate with you</div>
                    <div className='brand-onboarding-network-panel-other-networks'>
                      {merchantOptions?.length > 0
                        ? merchantOptions.map(merchant => {
                            const isSelected = preferredNetwork === merchant.source;

                            return (
                              <div className='brand-onboarding-network-panel-other-network-choice' key={merchant.id}>
                                <div className='brand-onboarding-network-panel-other-network-title'>{merchant.source.toUpperCase()}</div>

                                <div className='brand-onboarding-network-panel-other-network-rate'>
                                  at <span>{merchant.fullPayout}% commission</span>
                                </div>

                                <button
                                  className={cn('brand-onboarding-network-panel-button', { isSelected })}
                                  onClick={() => selectMerchant(merchant)}
                                >
                                  {isSelected ? 'selected' : 'select'}
                                </button>
                              </div>
                            );
                          })
                        : null}
                    </div>
                  </>
                ) : (
                  <div className='brand-onboarding-network-panel-no-merchants'>
                    *We could not find any existing affiliate network associated with your brand. If you have are integrated on an affiliate network
                    please allow ShopMy access to your network account and refresh this page. If you need help with this process, please contact our
                    support team.
                  </div>
                )}

                <div className='brand-onboarding-pagination-buttons'>
                  <button className='brand-onboarding-button stretch' onClick={() => props.goToPreviousPanel()}>
                    Back
                  </button>
                  <button className='brand-onboarding-button stretch primary' onClick={onNextClick}>
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

BrandOnboardingNetworkPanel.propTypes = {
  onboarding: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  updateBrandOnboardingObject: PropTypes.func.isRequired,

  goToPreviousPanel: PropTypes.func.isRequired,
  goToNextPanel: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  const { onboarding, user } = state;
  return { onboarding, user };
};

export default connect(mapStateToProps, {
  updateBrandOnboardingObject
})(withRouter(BrandOnboardingNetworkPanel));
